import { ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { toTestId } from './types';
import type { UseImportsReturn } from './useImportsManagement';

export const ImportPreview = ({ state }: { state: UseImportsReturn }) => {
  const { t } = useTranslation();
  const {
    importProgress,
    showFtpModal, ftpPath, setFtpPath, ftpFiles, ftpLoading, ftpError,
    loadFtpFiles, closeFtpModal, runFtpImport,
    sourceFilesModal, sourceFilesList, sourceFilesLoading,
    sourceFilesImportingId, closeSourceFilesModal, runImportFromSourceFile,
    runImportPendingFromSource, sourcePendingImportingDefinitionId,
    detailRunId, setDetailRunId, detailRun, setDetailRun,
  } = state;

  const downloadRunArtifact = async (runId: string, kind: 'failed-rows' | 'duplicate-rows') => {
    try {
      const res = await api.get(`/api/admin/imports/runs/${runId}/artifacts/${kind}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${kind}-${runId}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('imports.artifactDownloadError') );
    }
  };

  return (
    <>
      {/* Import progress (SSE) */}
      {importProgress && (
        <div className="rounded-lg border border-primary/50 bg-primary/5 p-4" role="status" aria-live="polite" data-testid="import-progress">
          <div className="flex items-center justify-between gap-4 mb-2">
            <span className="font-medium text-textDefault">
              {t('imports.importingProgress')} {importProgress.processed} / {importProgress.total || '?'} {t('imports.rowsSuffix')}
            </span>
            <span className="text-sm text-textSecondary">
              {t('copy.admin.importProgressCounts', {
          imported: importProgress.imported,
          updated: importProgress.updated,
          skipped: importProgress.skipped,
          failed: importProgress.failed,
        })}
            </span>
          </div>
          {importProgress.total > 0 && (
            <div className="h-2 bg-bgElevated rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300"
                style={{ width: `${Math.min(100, (100 * importProgress.processed) / importProgress.total)}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Run detail modal */}
      {detailRunId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { setDetailRunId(null); setDetailRun(null); }}>
          <div className="bg-bgElevated rounded-xl border border-border w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-border flex justify-between items-center">
              <h3 className="text-lg font-semibold text-textDefault">{t('imports.runDetailsTitle')}</h3>
              <button type="button" onClick={() => { setDetailRunId(null); setDetailRun(null); }}
                className="text-textSecondary hover:text-textDefault">✕</button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              {detailRun ? (
                <>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <dt className="text-textSecondary">{t('imports.runDetailsStatus')}</dt>
                    <dd className="text-textDefault font-medium capitalize">{detailRun.status}</dd>
                    <dt className="text-textSecondary">{t('imports.runDetailsStarted')}</dt>
                    <dd className="text-textDefault">{detailRun.startedAt ? new Date(detailRun.startedAt).toLocaleString('sv-SE') : '—'}</dd>
                    <dt className="text-textSecondary">{t('imports.runDetailsEnded')}</dt>
                    <dd className="text-textDefault">{detailRun.completedAt ? new Date(detailRun.completedAt).toLocaleString('sv-SE') : '—'}</dd>
                    <dt className="text-textSecondary">{t('imports.runDetailsFile')}</dt>
                    <dd className="text-textDefault">{detailRun.sourceFilename ?? '—'}</dd>
                    <dt className="text-textSecondary">{t('imports.runDetailsRows')}</dt>
                    <dd className="text-textDefault">
                      {t('copy.admin.importRunRowCounts', {
            total: detailRun.rowsTotal,
            imported: detailRun.rowsImported,
            updated: detailRun.rowsUpdated,
            skipped: detailRun.rowsSkipped,
            failed: detailRun.rowsFailed,
          })}
                    </dd>
                  </dl>
                  {detailRun.errorLog && detailRun.errorLog.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-textDefault mb-2">{t('imports.runDetailsErrors')} ({detailRun.errorLog.length})</h4>
                      <ul className="text-sm text-textSecondary bg-bgBase rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
                        {detailRun.errorLog.slice(0, 50).map((err: { row?: number; message?: string; code?: string }, i: number) => (
                          <li key={i}>Rad {err.row ?? '?'}: {err.message ?? JSON.stringify(err)}</li>
                        ))}
                        {detailRun.errorLog.length > 50 && (
                          <li className="text-textSecondary">… {t('imports.runDetailsAndMore').replace('{n}', String(detailRun.errorLog.length - 50))}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {detailRun.warningLog && detailRun.warningLog.filter((w) => w.code === 'DUPLICATE_SKIPPED').length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-textDefault mb-2">
                        {t('imports.runDetailsDuplicates') } ({detailRun.warningLog.filter((w) => w.code === 'DUPLICATE_SKIPPED').length})
                      </h4>
                      <ul className="text-sm text-textSecondary bg-bgBase rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
                        {detailRun.warningLog
                          .filter((w) => w.code === 'DUPLICATE_SKIPPED')
                          .slice(0, 50)
                          .map((warn, i) => (
                            <li key={i}>Rad {warn.row ?? '?'}: {warn.message ?? JSON.stringify(warn)}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {(detailRun.artifacts?.failedRowsPath || detailRun.artifacts?.duplicateRowsPath) && (
                    <div className="flex flex-wrap gap-2">
                      {detailRun.artifacts.failedRowsPath && (
                        <button
                          type="button"
                          onClick={() => downloadRunArtifact(detailRun.id, 'failed-rows')}
                          className="px-3 py-1.5 text-sm bg-bgBase border border-border rounded-lg hover:bg-primary/10 text-textDefault"
                          data-testid="download-failed-rows"
                        >
                          {t('imports.downloadFailedRows') }
                        </button>
                      )}
                      {detailRun.artifacts.duplicateRowsPath && (
                        <button
                          type="button"
                          onClick={() => downloadRunArtifact(detailRun.id, 'duplicate-rows')}
                          className="px-3 py-1.5 text-sm bg-bgBase border border-border rounded-lg hover:bg-primary/10 text-textDefault"
                          data-testid="download-duplicate-rows"
                        >
                          {t('imports.downloadDuplicateRows') }
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-textSecondary">{t('common.loading')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FTP Modal */}
      {showFtpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bgElevated rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-semibold text-textDefault">{t('imports.ftpImportTitle')}</h2>
              <button onClick={closeFtpModal} className="text-textSecondary hover:text-textDefault">✕</button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-4 gap-3 items-end">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-textSecondary mb-1">{t('imports.ftpPathLabel')}</label>
                  <input type="text" value={ftpPath} onChange={(e) => setFtpPath(e.target.value)}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder="/" data-testid="ftp-path-input" />
                </div>
                <button onClick={() => loadFtpFiles()} className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                  data-testid="ftp-refresh-button">{t('imports.ftpListBtn')}</button>
              </div>

              {ftpError && <div className="text-sm text-red-500" data-testid="ftp-error">{ftpError}</div>}

              <div className="bg-bgBase rounded-lg border border-border">
                <div className="divide-y divide-border">
                  {ftpLoading && <div className="p-4 text-textSecondary">{t('imports.ftpLoadingText')}</div>}
                  {!ftpLoading && ftpFiles.length === 0 && <div className="p-4 text-textSecondary">{t('imports.ftpNoFiles')}</div>}
                  {!ftpLoading && ftpFiles.map((file) => (
                    <div key={file.path} className="p-3 flex items-center justify-between">
                      <div>
                        <div className="text-textDefault font-medium">
                          {file.name}
                          {file.type === 'directory' && <span className="ml-2 text-xs text-textSecondary">[DIR]</span>}
                        </div>
                        <div className="text-xs text-textSecondary">
                          {file.size ? `${file.size} bytes` : ''}{file.modifiedAt ? ` • ${new Date(file.modifiedAt).toLocaleString('sv-SE')}` : ''}
                        </div>
                      </div>
                      {file.type === 'directory' ? (
                        <button onClick={() => loadFtpFiles(file.path)}
                          className="px-3 py-1 text-sm bg-bgElevated border border-border rounded-lg hover:bg-primary/10"
                          data-testid={`ftp-open-${toTestId(file.name)}`}>{t('imports.ftpOpen')}</button>
                      ) : (
                        <button onClick={() => runFtpImport(file.path)}
                          className="px-3 py-1 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                          data-testid={`ftp-run-${toTestId(file.name)}`}>{t('imports.ftpRunImport')}</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button onClick={closeFtpModal} className="px-4 py-2 text-textSecondary hover:text-textDefault">{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Source files modal */}
      {sourceFilesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bgElevated rounded-xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-border flex justify-between items-center gap-3">
              <h2 className="text-xl font-semibold text-textDefault">
                {t('imports.filesFromSourceTitle')} – {sourceFilesModal.definitionName}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => runImportPendingFromSource(sourceFilesModal.definitionId)}
                  disabled={sourcePendingImportingDefinitionId === sourceFilesModal.definitionId}
                  className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('imports.runPendingSourceTitle')}
                  data-testid={`run-pending-source-modal-${sourceFilesModal.definitionId}`}
                >
                  {sourcePendingImportingDefinitionId === sourceFilesModal.definitionId
                    ? t('imports.pendingImportRunning')
                    : t('imports.runPendingSource')}
                </button>
                <button onClick={closeSourceFilesModal} className="text-textSecondary hover:text-textDefault">✕</button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {sourceFilesLoading ? (
                <div className="flex items-center justify-center py-8 text-textSecondary">
                  <ArrowPathIcon className="w-8 h-8 animate-spin mr-2" />{t('imports.loadingFiles')}
                </div>
              ) : sourceFilesList.length === 0 ? (
                <div className="py-8 text-center text-textSecondary">
                  {t('imports.noFilesForSource')}
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-bgBase border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colName')}</th>
                        <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colDate')}</th>
                        <th className="px-4 py-3 text-left font-medium text-textSecondary">{t('imports.colSize')}</th>
                        <th className="px-4 py-3 text-right font-medium text-textSecondary">{t('imports.colAction')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sourceFilesList.map((file) => (
                        <tr key={file.id} className="hover:bg-bgBase/50">
                          <td className="px-4 py-3 text-textDefault font-medium truncate max-w-[200px]" title={file.original_filename}>
                            {file.original_filename}
                          </td>
                          <td className="px-4 py-3 text-textSecondary">
                            {file.file_date ? new Date(file.file_date).toLocaleString('sv-SE') : '-'}
                          </td>
                          <td className="px-4 py-3 text-textSecondary">
                            {file.file_size != null ? `${(file.file_size / 1024).toFixed(1)} KB` : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button type="button"
                              onClick={() => runImportFromSourceFile(sourceFilesModal.definitionId, file.id)}
                              disabled={sourceFilesImportingId === file.id}
                              className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid={`import-source-file-${file.id}`}>
                              {sourceFilesImportingId === file.id ? t('imports.importingFile') : t('imports.importThisFile')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button onClick={closeSourceFilesModal} className="px-4 py-2 text-textSecondary hover:text-textDefault">{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
